import type { ServiceCenter } from "../../types/serviceCenter.type";
import type {
  Equipment,
  EquipmentBucket,
  EquipmentOptionDisplay,
  EquipmentResponse,
  LoadSummary,
} from "./EquipmentDropdown.types";

type SicLookupFn = (sicId: number) => string;

export const createSicCodeMap = (serviceCenters: ServiceCenter[]) =>
  new Map(serviceCenters.map((sc) => [sc.id, sc.code]));

export const getSicCodeFromId = (
  sicId: number,
  sicCodeMap: Map<number, string>
) => sicCodeMap.get(sicId) ?? sicId.toString();

const formatStatusDisplay = (
  equipment: Equipment
): { at?: string; from?: string; to?: string; name: string } => {
  const statusName = equipment.status?.name ?? "";

  if (!equipment.assignedTo) {
    return {
      at: equipment.homeSicId.toString(),
      name: statusName,
    };
  }

  if (
    equipment.assignedTo.type === "Route" ||
    equipment.assignedTo.type === "Trap"
  ) {
    return {
      at: equipment.homeSicId.toString(),
      name: statusName,
    };
  }

  // Trip/Lane: Show origin → destination
  const originSic =
    equipment.assignedTo.linehaulInfo?.originSicId?.toString() ?? "Unknown";
  const destSic =
    equipment.assignedTo.linehaulInfo?.destinationSicId?.toString() ??
    "Unknown";

  return {
    from: originSic,
    to: destSic,
    name: statusName,
  };
};

const formatLocationDisplay = (equipment: Equipment): string => {
  if (!equipment.assignedTo) {
    return equipment.homeSicId.toString();
  }

  if (
    equipment.assignedTo.type === "Route" ||
    equipment.assignedTo.type === "Trap"
  ) {
    return `${equipment.homeSicId} - ${equipment.assignedTo.name}`;
  }

  // Trip/Lane: Show origin → destination
  const originSic =
    equipment.assignedTo.linehaulInfo?.originSicId?.toString() ?? "Unknown";
  const destSic =
    equipment.assignedTo.linehaulInfo?.destinationSicId?.toString() ??
    "Unknown";
  return `${originSic} → ${destSic} - ${equipment.assignedTo.name}`;
};

export const formatLocationWithSicCodes = (
  equipment: Equipment,
  getSicCode: SicLookupFn
): string => {
  if (!equipment.assignedTo) {
    return getSicCode(equipment.homeSicId);
  }

  if (
    equipment.assignedTo.type === "Route" ||
    equipment.assignedTo.type === "Trap"
  ) {
    const sicCode = getSicCode(equipment.homeSicId);
    return `${sicCode} - ${equipment.assignedTo.name}`;
  }

  // Trip/Lane: Show origin → destination with SIC codes
  const originCode = equipment.assignedTo.linehaulInfo?.originSicId
    ? getSicCode(equipment.assignedTo.linehaulInfo.originSicId)
    : "Unknown";
  const destCode = equipment.assignedTo.linehaulInfo?.destinationSicId
    ? getSicCode(equipment.assignedTo.linehaulInfo.destinationSicId)
    : "Unknown";
  return `${originCode} → ${destCode} - ${equipment.assignedTo.name}`;
};

const formatContentDisplay = (loadSummary: LoadSummary): string | null => {
  const { bills, pallets, pieces, weight } = loadSummary;

  if (bills === 0 && pallets === 0 && pieces === 0 && weight === 0) {
    return null;
  }

  const parts: string[] = [];

  if (bills > 0) parts.push(`${bills} Bills`);
  if (pallets > 0) parts.push(`${pallets} Plts`);
  if (pieces > 0) parts.push(`${pieces} Pcs`);
  if (weight > 0) {
    const formattedWeight = weight.toLocaleString("en-US");
    parts.push(`${formattedWeight} lbs`);
  }

  return parts.length > 0 ? parts.join(" | ") : null;
};

const transformEquipmentToDisplay = (
  equipment: Equipment,
  isAvailable: boolean,
  bucket: EquipmentBucket,
  getSicCode?: SicLookupFn
): EquipmentOptionDisplay => {
  const statusDisplay = formatStatusDisplay(equipment);

  const locationDisplay = getSicCode
    ? formatLocationWithSicCodes(equipment, getSicCode)
    : formatLocationDisplay(equipment);

  const contentDisplay = formatContentDisplay(equipment.loadSummary);

  return {
    id: equipment.equipmentId,
    refNumber: equipment.refNumber,
    statusName: statusDisplay.name,
    locationDisplay,
    contentDisplay,
    isAvailable,
    bucket,
    rawData: equipment,
  };
};

export const transformBackendResponse = (
  response: EquipmentResponse,
  getSicCode?: SicLookupFn
): EquipmentOptionDisplay[] => [
  ...response.available.map((eq) =>
    transformEquipmentToDisplay(eq, true, "available", getSicCode)
  ),
  ...response.assigned.map((eq) =>
    transformEquipmentToDisplay(eq, false, "assigned", getSicCode)
  ),
  ...response.loadAndHold.map((eq) =>
    transformEquipmentToDisplay(eq, false, "loadAndHold", getSicCode)
  ),
];
